import { Button } from 'antd';
import calendarPng from 'assets/images/calendar.png';
import InputUploadedFile from 'components/Atoms/Inputs/InputUploadedFile/InputUploadedFile';
import StateTag from 'components/Atoms/Tags/StateTag/StateTag';
import UploadDocs from 'components/Atoms/UploadDocs/UploadDocs';
import arrowPng from 'assets/images/arrow-blue.png';
import { TaskState } from 'gql/graphql';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ModalEndSubmit from '../../../Organisms/Modals/ModalEndSubmit/ModalEndSubmit';
import { Button as ButtonAtom } from 'components/Atoms/Buttons';
import { useFileUploadSync } from '@common/hooks/useFileUploadSync';

interface Props {
  title: string;
  description?: string;
  isButtonSample?: { show: boolean; link: string };
  isButtonTemplate?: { show: boolean; link: string };
  isDescriptionButton?: { show: boolean; name: string; link: string };
  submit?: {
    title: string;
    confirmDescription?: string;
    modalConfirm?: boolean;
    loading: boolean;
    onClick: (files: File[]) => void;
    location?: 'outside' | 'inside' | undefined;
    disabled?: boolean;
    ignoreDisabledNotFiles?: boolean;
  };
  limitFiles?: number;
  files?: File[] | any;
  state?: TaskState;
  hiddenTagDraft?: boolean;
  actionDeleteFile?: any;
  isEditDocuments?: boolean;
  onChangeFiles?: any;
  insertHtmlBottom?: any;
  uploadQueueFiles?: any[];
  uploadQueue?: any;
}

const CardUploadResponse = ({
  title,
  description,
  isButtonSample,
  isButtonTemplate,
  isDescriptionButton,
  submit,
  limitFiles = 1,
  files,
  state,
  actionDeleteFile,
  isEditDocuments,
  onChangeFiles,
  hiddenTagDraft,
  insertHtmlBottom,
  uploadQueueFiles = [],
  uploadQueue
}: Props) => {

  const allowContinuousUpload = limitFiles > 1;
  const [isModalSubmit, setModalSubmit] = useState(false);
  const hasSubmittedRef = useRef(false);

  // For continuous upload mode: use useFileUploadSync to merge backend + queue files
  const { enrichedFiles, setListFileUpload } = useFileUploadSync({
    files: allowContinuousUpload ? files : undefined,
    uploadQueueFiles,
    allowContinuousUpload,
    uploadQueue,
    onChangeFiles: allowContinuousUpload ? onChangeFiles : undefined
  });

  // For single-file mode: simple internal state (existing behavior)
  const [simpleListFileUpload, setSimpleListFileUpload] = useState<any[]>([]);

  useEffect(() => {
    if (!allowContinuousUpload && onChangeFiles) onChangeFiles(simpleListFileUpload);
  }, [simpleListFileUpload]);

  const activeFileList = allowContinuousUpload ? enrichedFiles : simpleListFileUpload;
  const activeSetFileList = allowContinuousUpload ? setListFileUpload : setSimpleListFileUpload;

  // In continuous mode: enabled only when there are new (local) files not yet uploaded or queued
  const hasNewFiles = allowContinuousUpload
    ? activeFileList.some((f: any) => !f.key && !f.isTemporary)
    : activeFileList.length > 0;

  // Memoize uploadQueueFiles with retry for passing to UploadDocs
  const uploadQueueFilesWithRetry = useMemo(() => {
    return uploadQueueFiles.map(f => ({
      ...f,
      onRetry: f.onRetry ?? (() => uploadQueue?.retryFile?.(f.id))
    }));
  }, [uploadQueueFiles, uploadQueue]);

  // Only unlock when the task state actually changes (backend processed the submit)
  useEffect(() => {
    hasSubmittedRef.current = false;
  }, [state]);

  const onSubmit = () => {
    if (hasSubmittedRef.current) return;
    if (!hasNewFiles) return;
    hasSubmittedRef.current = true;
    const dontShowKey = 'card-upload-response-enity-docs';
    const isDisabled = dontShowKey && localStorage.getItem(dontShowKey) === 'true';
    if (submit?.modalConfirm && !isDisabled) setModalSubmit(true);
    else submit?.onClick(activeFileList as File[]);
  };

  const handleModalClose = (visible: boolean) => {
    setModalSubmit(visible);
    if (!visible) hasSubmittedRef.current = false;
  };

  const onConfirmSubmit = () => {
    setModalSubmit(false);
    submit?.onClick(activeFileList as File[]);
  };

  return (
    <>
      <div className={`card-upload-response card-upload-response-${state}`}>
        {state && (state !== TaskState.Review && !hiddenTagDraft) && <StateTag state={state} customClass='mb-10' />}
        {state && (state === TaskState.Review) && <StateTag state={state} isLabelLocked customClass='mb-10' />}
        <div className="header" style={{ marginTop: !isButtonSample ? 5 : 0 }}>
          <span className="h4">{title}</span>
          {isButtonSample?.show && (
            <Button type="link" className="link" onClick={() => window.open(isButtonSample.link, '_blank')}>
              See sample
            </Button>
          )}
        </div>
        <div className="description">
          {description} {' '}
          {isDescriptionButton?.show && (
            <Button type="link" className="link description-button-link" onClick={() => window.open(isDescriptionButton?.link, '_blank')}>
              {isDescriptionButton?.name}
            </Button>
          )}
        </div>
        {isButtonTemplate?.show && (
          <div className="template">
            <Button type="link" className="button button-blue" onClick={() => window.open(isButtonTemplate.link, '_blank')}>
              Download template
            </Button>
            <img src={arrowPng} className="arrow-right" alt="arrow" />
          </div>
        )}
        <div className="section-upload">
          {/* Single-file mode: show saved files separately, upload area when no files */}
          {!allowContinuousUpload && files && (
            <div className="cont-files">
              {files?.filter((item: any) => item.state !== 'request_error').map((file: any, index: number) => (
                <InputUploadedFile
                  key={index}
                  file={file}
                  isActionSeeFile
                  state={state ? file.state : null}
                  deleteFile={
                    isEditDocuments ? actionDeleteFile :
                    (file.state !== 'approved' && file.state !== 'review') ?
                    actionDeleteFile : null
                  }
                />
              ))}
            </div>
          )}

          {(
            (!allowContinuousUpload && (!files || files?.length === 0 || state === TaskState.RequestError)) ||
            (allowContinuousUpload && (enrichedFiles.length < limitFiles || state === TaskState.RequestError))
          ) && (
            <UploadDocs
              numberOfFiles={limitFiles}
              loading={submit?.loading}
              listFileUpload={activeFileList}
              setListFileUpload={activeSetFileList}
              responsesFiles={files}
              uploadQueueFiles={uploadQueueFilesWithRetry}
              button={
                submit && (!submit.location || submit.location === 'inside') ? (
                  <ButtonAtom.Primary
                    className="button button-submit"
                    loading={submit.loading}
                    disabled={!hasNewFiles || submit.disabled || hasSubmittedRef.current}
                    onClick={onSubmit}
                  >
                    {submit.title}
                  </ButtonAtom.Primary>
                ) : null
              }
            />
          )}
        </div>

        {submit?.modalConfirm && (
          <ModalEndSubmit
            visible={isModalSubmit}
            setVisible={handleModalClose}
            buttonAction={{
              hidden: true
            }}
            modal={{
              title: 'Ready to submit?',
              description: submit?.confirmDescription ? (
                <h4 className="h4 h4-w4">{submit?.confirmDescription}</h4>
              ) : undefined
            }}
            buttonSubmit={
              <ButtonAtom.Primary
                className="button button-modal-ok"
                onClick={onConfirmSubmit}
                loading={submit.loading}
              >
                Yes, this is correct
              </ButtonAtom.Primary>
            }
            buttonCancel={(actionCancel: any) => (
              <Button
                className="button button-modal-cancel"
                onClick={actionCancel}
              >
                I want to make changes
              </Button>
            )}
            icon={calendarPng}
            className="modal-end-submit--white"
            checkboxDontShow={{
              label: 'Don’t show this message again for this task',
              storageKey: `card-upload-response-enity-docs`,
              autoSubmit: onConfirmSubmit
            }}
          />
        )}
        {insertHtmlBottom}
        
      </div>
      {submit?.location === 'outside' && (
        <div className="mt-20">
          <Button
            className="button button-submit"
            loading={submit?.loading}
            disabled={(!hasNewFiles && !submit.ignoreDisabledNotFiles) || submit.disabled || hasSubmittedRef.current}
            onClick={onSubmit}
          >
            {submit?.title}
          </Button>
        </div>
      )}
    </>
  );
};
export default CardUploadResponse;
